import { clamp01, type V2 } from '../core/math';
import type { Mixer } from './mixer';
import { jitterGain, jitterRatio } from './mix';
import { distanceCutoff, distanceGain, farFade, panFor, type Listener } from './spatial';
import { bellPartials, noiseBurst, ringTick, sweepNoise, tone } from './synth';

/**
 * World one-shots: impacts, prop hits, ped thuds, explosions, glass, doors, cash register,
 * lockpick, grunt, footsteps (by surface and weather), the horn and weapons (shots with street
 * slap-back, rounds landing by surface and the ricochet whine, swings, blows, reloads, the dry click,
 * screams). All synthesised; every node ends and disconnects.
 */

export type GunSound = 'revolver' | 'shotgun';
/** `wood` = a bat, `panel` = a melee blow on a car body. */
export type BlowSound = 'punch' | 'thud' | 'slash' | 'clang' | 'wood' | 'panel';
export type Surface = 'pavement' | 'setts' | 'grit' | 'stone';
/** A round landing, by what it found (the impact kit, src/fx/impactFx.ts). */
export type ImpactSound = 'brick' | 'plaster' | 'metal' | 'glass' | 'panel' | 'tarmac';

/** The impact kit's surface classes to a sound family; null for the ones that make none here (a person: the hit marker's thwack). */
export function impactSoundFor(surface: string): ImpactSound | null {
  switch (surface) {
    case 'brick':
    case 'stone':
      return 'brick';
    case 'plaster':
      return 'plaster';
    case 'metal':
      return 'metal';
    case 'glass':
      return 'glass';
    case 'vehicle':
      return 'panel';
    case 'ground':
      return 'tarmac';
    default:
      return null;
  }
}

export type PropSoundKind = 'bin' | 'cone' | 'crate' | 'bollard' | 'barrier' | 'generic';

/** Map a prop kind string (from `propHit`) to a sound family. */
export function propSoundKind(kind: string): PropSoundKind {
  const k = kind.toLowerCase();
  if (k.includes('bin') || k.includes('skip') || k.includes('can')) return 'bin';
  if (k.includes('cone')) return 'cone';
  if (k.includes('crate') || k.includes('box') || k.includes('pallet') || k.includes('barrel')) return 'crate';
  if (k.includes('bollard') || k.includes('post') || k.includes('lamp') || k.includes('hydrant')) return 'bollard';
  if (k.includes('barrier') || k.includes('fence') || k.includes('sign')) return 'barrier';
  return 'generic';
}

/** Ground underfoot by district (Cinder Yards' cinders, the cobbled terraces, the stone quays). */
export function surfaceForDistrict(district: string): Surface {
  switch (district) {
    case 'cinder-yards':
      return 'grit';
    case 'tanners-row':
      return 'setts';
    case 'gullet-basin':
      return 'stone';
    default:
      return 'pavement';
  }
}

/** Crash weight 0..1 from closing speed and impulse: a parking tap is ~0, a lorry at 20 m/s is 1. */
export function crashWeight(speed: number, impulse: number): number {
  const s = clamp01(((Number.isFinite(speed) ? speed : 0) - 1) / 16);
  const i = clamp01((Number.isFinite(impulse) ? impulse : 0) / 18000);
  return clamp01(0.6 * s + 0.4 * Math.sqrt(i));
}

const r = (lo: number, hi: number): number => lo + Math.random() * (hi - lo);

interface Horn {
  o1: OscillatorNode;
  o2: OscillatorNode;
  buzz: OscillatorNode;
  g: GainNode;
  nodes: AudioNode[];
}

export class OneShots {
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();
  private horn: Horn | null = null;
  private hornWave: PeriodicWave | null = null;
  private stepClock = 0;
  private stepFlip = false;

  constructor(
    private readonly mx: Mixer,
    private readonly listener: Listener,
  ) {}

  /**
   * Builds a positional output chain (gain -> [air lowpass] -> pan -> bus, plus an optional send into
   * the street slap-back) and runs `build` on it. Returns false (and builds nothing) if inaudible.
   */
  private shot(pos: V2 | null, bus: GainNode, level: number, life: number, build: (dest: AudioNode, t: number) => void, ref = 22, echo = 0): boolean {
    const ctx = this.mx.ctx;
    if (!ctx || !this.mx.running) return false;
    // +-1 dB per hit: no two footsteps, shots or bumps land at exactly the same level.
    let g = level * jitterGain(1);
    let pan = 0;
    let cutoff = 19000;
    if (pos) {
      const l = this.listener;
      const dx = pos.x - l.x;
      const dz = pos.z - l.z;
      const d = Math.sqrt(dx * dx + dz * dz);
      g *= distanceGain(d, ref) * farFade(d, ref);
      pan = panFor(dx, dz, l.rx, l.rz);
      cutoff = distanceCutoff(d);
    }
    if (g < 0.01) return false;
    const gain = ctx.createGain();
    gain.gain.value = g;
    const nodes: AudioNode[] = [gain];
    let tail: AudioNode = gain;
    if (cutoff < 16000) {
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = cutoff;
      tail.connect(lp);
      tail = lp;
      nodes.push(lp);
    }
    const p = ctx.createStereoPanner();
    p.pan.value = pan;
    tail.connect(p).connect(bus);
    nodes.push(p);
    if (echo > 0 && this.mx.buses.echo) {
      const send = ctx.createGain();
      send.gain.value = echo;
      tail.connect(send).connect(this.mx.buses.echo);
      nodes.push(send);
    }
    build(gain, ctx.currentTime + 0.005);
    const timer = setTimeout(
      () => {
        this.timers.delete(timer);
        for (const n of nodes) n.disconnect();
      },
      (life + 0.3) * 1000,
    );
    this.timers.add(timer);
    return true;
  }

  /**
   * Vehicle crash with weight: a sub thump sized by the impact, a crumple of short metal grains, a
   * dull body boom and (hard hits) a panel ring and a scatter of trim.
   */
  impact(speed: number, impulse: number, pos: V2): void {
    const mx = this.mx;
    const w = crashWeight(speed, impulse);
    this.shot(
      pos,
      mx.buses.sfx,
      0.4 + 0.8 * w,
      1.2,
      (d, t) => {
        // Body thump: lower and longer the heavier the hit.
        tone(mx, { type: 'sine', f0: 120 - 50 * w, f1: 34, sweepTime: 0.18 + 0.12 * w, t, dur: 0.28 + 0.3 * w, gain: 0.6 + 0.35 * w, attack: 0.002, dest: d });
        noiseBurst(mx, { kind: 'brown', t, dur: 0.16 + 0.22 * w, gain: 0.35 + 0.55 * w, attack: 0.002, filter: 'lowpass', f0: 650, f1: 120, dest: d });
        // Crumple: a cluster of short, differently-pitched metal grains.
        const grains = 2 + Math.round(5 * w);
        for (let i = 0; i < grains; i++) {
          const at = t + (i === 0 ? 0 : r(0.004, 0.06 + 0.12 * w));
          noiseBurst(mx, { kind: 'white', t: at, dur: r(0.02, 0.06), gain: (0.24 + 0.4 * w) * r(0.6, 1), filter: 'bandpass', f0: r(700, 2600), q: r(1.5, 4), dest: d });
        }
        if (w > 0.25) bellPartials(mx, d, t, r(260, 420), 0.25 + 0.35 * w, 0.06 + 0.08 * w, [1, 2.31, 3.9, 5.7]);
        if (w > 0.55) {
          // Bits of trim and glass skittering after the hit.
          for (let i = 0; i < 4; i++) ringTick(mx, d, t + r(0.08, 0.35), r(2500, 5200), 9, r(0.05, 0.1), 0.05);
        }
      },
      22,
      w > 0.5 ? 0.25 : 0,
    );
  }

  /** Lighter prop sounds, kind-dependent. */
  propHit(kind: string, speed: number, pos: V2): void {
    const mx = this.mx;
    const s = clamp01(speed / 10);
    const k = propSoundKind(kind);
    this.shot(pos, mx.buses.sfx, 0.3 + 0.5 * s, 0.7, (d, t) => {
      switch (k) {
        case 'bin':
          // Dustbin: tinny clang + lid rattle.
          bellPartials(mx, d, t, r(200, 260), 0.55, 0.24 + 0.18 * s, [1, 2.3, 4.1, 6.6]);
          noiseBurst(mx, { kind: 'white', t, dur: 0.07, gain: 0.25, filter: 'bandpass', f0: 1500, q: 1.2, dest: d });
          for (let i = 0; i < 3; i++) ringTick(mx, d, t + 0.1 + i * r(0.05, 0.09), r(900, 1300), 6, 0.08 * (1 - i * 0.25), 0.05);
          break;
        case 'cone':
          tone(mx, { type: 'sine', f0: r(430, 520), f1: 210, sweepTime: 0.07, t, dur: 0.09, gain: 0.55, dest: d });
          noiseBurst(mx, { kind: 'pink', t, dur: 0.05, gain: 0.22, filter: 'bandpass', f0: 1800, q: 1, dest: d });
          break;
        case 'crate':
          // Splintering wood: a hollow knock and a crackle of splinters.
          ringTick(mx, d, t, r(240, 320), 6, 0.6, 0.12);
          tone(mx, { type: 'triangle', f0: r(170, 210), f1: 90, sweepTime: 0.1, t, dur: 0.14, gain: 0.5, dest: d });
          for (let i = 0; i < 4; i++) noiseBurst(mx, { kind: 'white', t: t + r(0.01, 0.12), dur: 0.015, gain: 0.18, filter: 'bandpass', f0: r(1800, 3500), q: 2, dest: d });
          break;
        case 'bollard':
          tone(mx, { type: 'sine', f0: 140, f1: 60, sweepTime: 0.1, t, dur: 0.2, gain: 0.7, dest: d });
          bellPartials(mx, d, t, r(300, 340), 0.3, 0.12, [1, 2.5, 4.7]);
          break;
        case 'barrier':
          bellPartials(mx, d, t, r(500, 700), 0.45, 0.16, [1, 2.76, 5.4, 8.93]);
          noiseBurst(mx, { kind: 'white', t, dur: 0.14, gain: 0.25, filter: 'bandpass', f0: 2200, q: 1.5, dest: d });
          break;
        default:
          tone(mx, { type: 'sine', f0: 200, f1: 80, sweepTime: 0.08, t, dur: 0.15, gain: 0.6, dest: d });
          noiseBurst(mx, { kind: 'white', t, dur: 0.07, gain: 0.25, filter: 'bandpass', f0: 1400, q: 1, dest: d });
      }
    });
  }

  /** Body thud + a short breathy gasp. */
  pedHit(pos: V2, speed: number): void {
    const mx = this.mx;
    const s = clamp01(speed / 12);
    this.shot(pos, mx.buses.sfx, 0.5 + 0.4 * s, 0.6, (d, t) => {
      tone(mx, { type: 'sine', f0: 105, f1: 52, sweepTime: 0.1, t, dur: 0.18, gain: 0.8, dest: d });
      noiseBurst(mx, { kind: 'brown', t, dur: 0.1, gain: 0.35, filter: 'lowpass', f0: 700, dest: d });
      noiseBurst(mx, { kind: 'pink', t: t + 0.05, dur: 0.22, gain: 0.14, attack: 0.04, filter: 'bandpass', f0: 1900, f1: 900, q: 1.6, dest: d });
    });
  }

  /** Explosion: filtered noise burst sweeping down + sub sine, rolling off the terraces. Ducks engines + sirens. */
  boom(big: boolean, pos: V2): void {
    const mx = this.mx;
    const scale = big ? 1 : 0.55;
    const ok = this.shot(
      pos,
      mx.buses.sfx,
      scale,
      2.6,
      (d, t) => {
        const dur = big ? 1.9 : 1.0;
        noiseBurst(mx, { kind: 'brown', t, dur, gain: 1.1, attack: 0.004, filter: 'lowpass', f0: 3000, f1: 80, q: 0.8, dest: d });
        noiseBurst(mx, { kind: 'white', t, dur: 0.22, gain: 0.45, filter: 'highpass', f0: 900, dest: d });
        tone(mx, { type: 'sine', f0: big ? 85 : 100, f1: 26, sweepTime: dur * 0.7, t, dur, gain: 1.0, attack: 0.006, dest: d });
        if (big) {
          noiseBurst(mx, { kind: 'pink', t: t + 0.18, dur: 1.2, gain: 0.25, attack: 0.1, filter: 'bandpass', f0: 400, f1: 150, q: 0.6, dest: d });
          // Debris raining down.
          for (let i = 0; i < 6; i++) ringTick(mx, d, t + r(0.35, 1.3), r(1500, 4500), 7, r(0.04, 0.09), 0.05);
        }
      },
      30,
      big ? 0.6 : 0.35,
    );
    if (ok && big) {
      const l = this.listener;
      const amt = 0.6 * distanceGain(Math.hypot(pos.x - l.x, pos.z - l.z), 30);
      mx.duckVehicles(amt, 0.8);
      mx.duck(amt * 0.7, 1.2);
    }
  }

  /**
   * A pre-baked buffer (the ZzFX presets, sfxBank.ts) through the same positional chain, with an optional
   * lowpass to soften it and a playback `rate` (pitch variation). `pos` null = at the listener.
   */
  buffer(buf: AudioBuffer, pos: V2 | null, bus: GainNode, level: number, lp?: number, ref = 22, echo = 0, rate = 1): void {
    const mx = this.mx;
    if (!mx.canSpawn()) return;
    this.shot(
      pos,
      bus,
      level,
      buf.duration / rate,
      (d, t) => {
        const ctx = mx.ctx!;
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.playbackRate.value = rate;
        let head: AudioNode = src;
        let f: BiquadFilterNode | null = null;
        if (lp) {
          f = ctx.createBiquadFilter();
          f.type = 'lowpass';
          f.frequency.value = lp;
          src.connect(f);
          head = f;
        }
        head.connect(d);
        mx.voiceCount++;
        src.onended = () => {
          mx.voiceCount--;
          src.disconnect();
          f?.disconnect();
        };
        src.start(t);
      },
      ref,
      echo,
    );
  }

  /** Glass tinkle for big vehicle damage. */
  glass(pos: V2): void {
    const mx = this.mx;
    this.shot(pos, mx.buses.sfx, 1.0, 0.7, (d, t) => {
      noiseBurst(mx, { kind: 'white', t, dur: 0.05, gain: 0.25, filter: 'highpass', f0: 3000, dest: d });
      for (let i = 0; i < 8; i++) {
        const dt = i * r(0.015, 0.06);
        ringTick(mx, d, t + dt, r(3000, 7500), 18, r(0.05, 0.12), r(0.05, 0.12));
      }
    });
  }

  /** Door: latch click, a solid thunk of a heavy 1970 door. */
  door(pos: V2 | null): void {
    const mx = this.mx;
    const p = jitterRatio(90);
    this.shot(pos, mx.buses.sfx, 0.55, 0.4, (d, t) => {
      ringTick(mx, d, t, 2300 * p, 6, 0.12, 0.02);
      tone(mx, { type: 'sine', f0: 160 * p, f1: 68 * p, sweepTime: 0.08, t: t + 0.02, dur: 0.15, gain: 0.7, dest: d });
      noiseBurst(mx, { kind: 'pink', t: t + 0.02, dur: 0.07, gain: 0.28, filter: 'bandpass', f0: 420, q: 1, dest: d });
    });
  }

  /** "Cha-ching": drawer clack then bright double bell. */
  cashRegister(): void {
    const mx = this.mx;
    this.shot(null, mx.buses.ui, 0.8, 0.9, (d, t) => {
      noiseBurst(mx, { kind: 'white', t, dur: 0.05, gain: 0.3, filter: 'bandpass', f0: 3200, q: 1.5, dest: d });
      tone(mx, { type: 'triangle', f0: 180, f1: 90, sweepTime: 0.06, t, dur: 0.1, gain: 0.35, dest: d });
      const p = jitterRatio(25);
      bellPartials(mx, d, t + 0.08, 2093 * p, 0.7, 0.28, [1, 1.5, 2.01]);
      bellPartials(mx, d, t + r(0.15, 0.19), 3136 * p, 0.8, 0.22, [1, 1.5, 2.01]);
    });
  }

  /** Lockpick / glass clink for carjacks and break-ins. */
  clink(pos: V2 | null): void {
    const mx = this.mx;
    this.shot(pos, mx.buses.sfx, 0.85, 0.5, (d, t) => {
      tone(mx, { type: 'sine', f0: 3300, t, dur: 0.12, gain: 0.22, attack: 0.001, dest: d });
      tone(mx, { type: 'sine', f0: 4500, t: t + 0.09, dur: 0.1, gain: 0.18, attack: 0.001, dest: d });
      noiseBurst(mx, { kind: 'white', t, dur: 0.015, gain: 0.2, filter: 'highpass', f0: 6000, dest: d });
    });
  }

  /**
   * Gunshot: a hard crack, a chesty N-wave thump, a mid bark and a rolling tail, plus a send into the
   * street slap-back. The sawn-off is lower, wider and longer. `pos` null = our own gun.
   */
  gunshot(kind: GunSound, pos: V2 | null): void {
    const mx = this.mx;
    const sg = kind === 'shotgun';
    // Each shot a little different: +-70 cents on the body, the bark and the crack, the tail +-20 %.
    const p = jitterRatio(70);
    const tail = r(0.8, 1.2);
    const ok = this.shot(
      pos,
      mx.buses.weapons,
      1.0,
      2,
      (d, t) => {
        noiseBurst(mx, { kind: 'white', t, dur: sg ? 0.012 : 0.007, gain: 1.0, attack: 0.0005, filter: 'highpass', f0: (sg ? 1500 : 2500) * p, dest: d });
        tone(mx, { type: 'sine', f0: (sg ? 125 : 175) * p, f1: (sg ? 34 : 48) * p, sweepTime: sg ? 0.09 : 0.045, t, dur: sg ? 0.3 : 0.16, gain: sg ? 1.05 : 0.85, attack: 0.0008, dest: d });
        noiseBurst(mx, { kind: 'brown', t, dur: sg ? 0.38 : 0.22, gain: sg ? 1.0 : 0.95, attack: 0.001, filter: 'lowpass', f0: (sg ? 2200 : 3000) * p, f1: sg ? 110 : 180, q: 0.7, dest: d });
        noiseBurst(mx, { kind: 'pink', t, dur: sg ? 0.1 : 0.06, gain: sg ? 0.55 : 0.45, attack: 0.001, filter: 'bandpass', f0: (sg ? 650 : 950) * p, q: 1, dest: d });
        // The tail rolling away down the street.
        noiseBurst(mx, { kind: 'pink', t: t + 0.03, dur: (sg ? 1.6 : 1.15) * tail, gain: 0.2, attack: 0.025, filter: 'lowpass', f0: 1300 * p, f1: 140, dest: d });
      },
      40,
      sg ? 0.85 : 0.7,
    );
    if (ok) {
      mx.duck(sg ? 0.35 : 0.25, 0.4);
      mx.duckMusic(0.3, 2.5);
    }
  }

  /**
   * The swish of a swing: a band sweeping up and down past the ear. Heavy weapons (bat, crowbar) are
   * lower, longer and fuller; a fist or blade is a quick hiss.
   */
  swing(weight: number): void {
    const mx = this.mx;
    const w = clamp01(weight);
    const p = jitterRatio(120);
    this.shot(null, mx.buses.weapons, 0.3 + 0.3 * w, 0.6, (d, t) => {
      const dur = (0.14 + 0.18 * w) * r(0.9, 1.1);
      sweepNoise(mx, { kind: 'pink', t, dur, gain: 0.5, f0: (350 + 300 * (1 - w)) * p, fPeak: (1100 + 1300 * (1 - w)) * p, f1: (300 + 200 * (1 - w)) * p, peakAt: r(0.5, 0.65), q: 1.8 + w, dest: d });
      if (w > 0.6) sweepNoise(mx, { kind: 'brown', t, dur, gain: 0.35, f0: 120, fPeak: 260, f1: 110, peakAt: 0.6, q: 1.2, dest: d });
    });
  }

  /** A blow landing: knuckles, a cosh thud, a bat's wooden tock, a crowbar clang, a blade, a car panel. */
  blow(kind: BlowSound, pos: V2): void {
    const mx = this.mx;
    // The body thuds move +-1 semitone between blows (`b` scales the low tones below).
    const b = jitterRatio(100);
    this.shot(pos, mx.buses.weapons, 0.85, 0.6, (d, t) => {
      switch (kind) {
        case 'punch':
          // Knuckles on a jaw: a skin slap and a short body thud.
          noiseBurst(mx, { kind: 'white', t, dur: 0.022, gain: 0.45, attack: 0.0008, filter: 'bandpass', f0: 1600, q: 1.1, dest: d });
          tone(mx, { type: 'sine', f0: 125 * b, f1: 58 * b, sweepTime: 0.06, t, dur: 0.11, gain: 0.85, attack: 0.001, dest: d });
          noiseBurst(mx, { kind: 'brown', t, dur: 0.07, gain: 0.35, filter: 'lowpass', f0: 600, dest: d });
          break;
        case 'thud':
          // A cosh: dull, heavy, no ring.
          tone(mx, { type: 'sine', f0: 100 * b, f1: 42 * b, sweepTime: 0.1, t, dur: 0.2, gain: 1.0, attack: 0.001, dest: d });
          noiseBurst(mx, { kind: 'brown', t, dur: 0.13, gain: 0.6, filter: 'lowpass', f0: 800, dest: d });
          noiseBurst(mx, { kind: 'pink', t, dur: 0.03, gain: 0.2, filter: 'bandpass', f0: 1100, q: 1, dest: d });
          break;
        case 'wood':
          // A cricket-bat tock: hollow wooden ring over the body thud.
          ringTick(mx, d, t, r(480, 560), 9, 0.7, 0.14);
          ringTick(mx, d, t, r(1150, 1300), 7, 0.25, 0.07);
          tone(mx, { type: 'sine', f0: 105 * b, f1: 46 * b, sweepTime: 0.1, t, dur: 0.17, gain: 0.85, attack: 0.001, dest: d });
          break;
        case 'clang':
          tone(mx, { type: 'sine', f0: 120 * b, f1: 50 * b, sweepTime: 0.1, t, dur: 0.16, gain: 0.7, dest: d });
          bellPartials(mx, d, t, r(700, 900), 0.45, 0.22, [1, 2.76, 5.4, 8.93]);
          break;
        case 'panel': {
          // A car body: a dull, boomy metal thump with a short ring. No two panels ring alike (the partial
          // set and its pitch move between blows) and one in three catches a brighter clang off an edge.
          const bright = r(0, 1) < 0.35;
          tone(mx, { type: 'sine', f0: 140 * b, f1: 70 * b, sweepTime: 0.08, t, dur: 0.22, gain: 0.8, dest: d });
          bellPartials(mx, d, t, r(200, 330), bright ? 0.36 : 0.3, bright ? 0.18 : 0.12, r(0, 1) < 0.5 ? [1, 2.31, 3.9] : [1, 2.05, 3.42, 5.1]);
          if (bright) ringTick(mx, d, t, r(1500, 2100), 8, 0.22, 0.06);
          noiseBurst(mx, { kind: 'white', t, dur: 0.04, gain: 0.25, filter: 'bandpass', f0: 1800, q: 1.3, dest: d });
          break;
        }
        case 'slash':
          sweepNoise(mx, { kind: 'white', t, dur: 0.1, gain: 0.35, f0: 2500, fPeak: 5000, f1: 1800, peakAt: 0.3, q: 2.5, dest: d });
          tone(mx, { type: 'sine', f0: 90 * b, f1: 50 * b, sweepTime: 0.08, t: t + 0.02, dur: 0.1, gain: 0.5, dest: d });
          noiseBurst(mx, { kind: 'pink', t: t + 0.02, dur: 0.08, gain: 0.25, filter: 'lowpass', f0: 900, dest: d });
          break;
      }
    });
  }

  /**
   * A round landing (the impact kit, src/fx/impactFx.ts): a brick thud and the crumble of mortar, a
   * plaster crack with grit pattering down, a metal ping with its hanging ring, a glass tick and the
   * crack running, a car panel's thunk, a skip off the tarmac. `pellets` > 1 (the sawn-off) thickens
   * it with a second, staggered hit.
   */
  bulletHit(kind: ImpactSound, pos: V2, pellets = 1): void {
    const mx = this.mx;
    const p = jitterRatio(90);
    const sg = pellets > 1;
    this.shot(
      pos,
      mx.buses.weapons,
      sg ? 0.9 : 0.7,
      0.7,
      (d, t) => {
        for (let h = 0; h < (sg ? 2 : 1); h++) {
          const at = t + (h === 0 ? 0 : r(0.012, 0.03));
          const g = h === 0 ? 1 : 0.6;
          switch (kind) {
            case 'brick':
              tone(mx, { type: 'sine', f0: 190 * p, f1: 70 * p, sweepTime: 0.05, t: at, dur: 0.1, gain: 0.7 * g, attack: 0.001, dest: d });
              noiseBurst(mx, { kind: 'white', t: at, dur: 0.018, gain: 0.5 * g, attack: 0.0005, filter: 'bandpass', f0: 2200 * p, q: 0.8, dest: d });
              noiseBurst(mx, { kind: 'pink', t: at, dur: 0.14, gain: 0.45 * g, attack: 0.002, filter: 'lowpass', f0: 1600, f1: 300, dest: d });
              for (let i = 0; i < 3; i++) ringTick(mx, d, at + r(0.03, 0.12), r(2800, 5200), 10, 0.08 * g, 0.03);
              break;
            case 'plaster':
              noiseBurst(mx, { kind: 'white', t: at, dur: 0.03, gain: 0.5 * g, attack: 0.0005, filter: 'bandpass', f0: 3000 * p, q: 0.9, dest: d });
              tone(mx, { type: 'sine', f0: 240 * p, f1: 90 * p, sweepTime: 0.04, t: at, dur: 0.07, gain: 0.4 * g, attack: 0.001, dest: d });
              noiseBurst(mx, { kind: 'pink', t: at + 0.01, dur: 0.2, gain: 0.35 * g, attack: 0.005, filter: 'lowpass', f0: 2600, f1: 500, dest: d });
              for (let i = 0; i < 4; i++) ringTick(mx, d, at + r(0.04, 0.18), r(3500, 7000), 12, 0.06 * g, 0.025);
              break;
            case 'metal':
              // The click of the strike and an inharmonic ring that hangs.
              noiseBurst(mx, { kind: 'white', t: at, dur: 0.01, gain: 0.6 * g, attack: 0.0004, filter: 'highpass', f0: 2500, dest: d });
              bellPartials(mx, d, at, r(1900, 2700) * p, 0.4, 0.28 * g, [1, 1.83, 2.95, 4.4]);
              tone(mx, { type: 'sine', f0: 260 * p, f1: 130 * p, sweepTime: 0.05, t: at, dur: 0.07, gain: 0.35 * g, attack: 0.001, dest: d });
              break;
            case 'glass':
              ringTick(mx, d, at, r(5200, 8000), 22, 0.5 * g, 0.06);
              noiseBurst(mx, { kind: 'white', t: at, dur: 0.025, gain: 0.3 * g, attack: 0.0005, filter: 'highpass', f0: 3800, dest: d });
              for (let i = 0; i < 3; i++) ringTick(mx, d, at + r(0.02, 0.09), r(3500, 7500), 18, 0.14 * g, 0.04);
              break;
            case 'panel':
              tone(mx, { type: 'sine', f0: 170 * p, f1: 80 * p, sweepTime: 0.07, t: at, dur: 0.16, gain: 0.75 * g, attack: 0.001, dest: d });
              bellPartials(mx, d, at, r(240, 320), 0.2, 0.16 * g, [1, 2.31, 3.9]);
              noiseBurst(mx, { kind: 'white', t: at, dur: 0.025, gain: 0.3 * g, attack: 0.0005, filter: 'bandpass', f0: 1700, q: 1.2, dest: d });
              break;
            case 'tarmac':
              // A zip of grit and a small dull slap.
              sweepNoise(mx, { kind: 'pink', t: at, dur: 0.09, gain: 0.3 * g, f0: 1400 * p, fPeak: 3600 * p, f1: 900, peakAt: 0.25, q: 2.2, dest: d });
              noiseBurst(mx, { kind: 'white', t: at, dur: 0.02, gain: 0.3 * g, attack: 0.0005, filter: 'highpass', f0: 1800, dest: d });
              noiseBurst(mx, { kind: 'brown', t: at, dur: 0.06, gain: 0.3 * g, filter: 'lowpass', f0: 700, dest: d });
              break;
          }
        }
      },
      18,
    );
  }

  /** The whine of a round singing off a wall or a car: a falling tone with a thin, high scrape, a touch of the street in it. */
  ricochet(pos: V2): void {
    const mx = this.mx;
    const p = jitterRatio(140);
    const dur = r(0.22, 0.36);
    this.shot(
      pos,
      mx.buses.weapons,
      0.45,
      dur + 0.2,
      (d, t) => {
        const at = t + r(0.015, 0.035);
        tone(mx, { type: 'sine', f0: 3100 * p, f1: 760 * p, sweepTime: dur, t: at, dur, gain: 0.45, attack: 0.004, release: 0.1, vib: { hz: 28, cents: 25 }, dest: d });
        tone(mx, { type: 'triangle', f0: 4600 * p, f1: 1500 * p, sweepTime: dur * 0.8, t: at, dur: dur * 0.8, gain: 0.12, attack: 0.004, lp: 6000, dest: d });
        sweepNoise(mx, { kind: 'white', t: at, dur: dur * 0.7, gain: 0.2, f0: 3800 * p, fPeak: 4300 * p, f1: 1400, peakAt: 0.12, q: 14, dest: d });
      },
      30,
      0.4,
    );
  }

  /**
   * Reload. Revolver: latch, cylinder swings out, ejector, six cases tinkling down, rounds pushed in,
   * cylinder snapped shut. Sawn-off: lever, the break-open clack, two hollow cases out, two in, the
   * heavy snap shut.
   */
  reload(kind: GunSound, seconds: number): void {
    const mx = this.mx;
    const sec = Math.max(0.6, seconds);
    this.shot(null, mx.buses.weapons, 0.6, sec + 0.4, (d, t) => {
      const click = (at: number, f: number, g: number, q = 5): void => ringTick(mx, d, at, f, q, g, 0.03);
      if (kind === 'revolver') {
        click(t + 0.03, 3200, 0.45);
        click(t + 0.12, 1900, 0.55, 4); // swing-out
        noiseBurst(mx, { kind: 'pink', t: t + 0.22, dur: 0.05, gain: 0.25, filter: 'bandpass', f0: 2600, q: 2, dest: d }); // ejector
        for (let i = 0; i < 6; i++) ringTick(mx, d, t + 0.3 + i * r(0.02, 0.05), r(3800, 5600), 22, r(0.06, 0.11), 0.09); // cases
        for (let i = 0; i < 6; i++) click(t + sec * (0.42 + (0.4 * i) / 6), r(2400, 2900), 0.28, 3);
        click(t + sec * 0.9, 1500, 0.7, 3); // snap shut
        tone(mx, { type: 'sine', f0: 240, f1: 120, sweepTime: 0.03, t: t + sec * 0.9, dur: 0.05, gain: 0.25, dest: d });
      } else {
        click(t + 0.03, 2200, 0.5, 4); // lever
        click(t + 0.1, 1100, 0.7, 3); // break open
        tone(mx, { type: 'sine', f0: 180, f1: 90, sweepTime: 0.04, t: t + 0.1, dur: 0.07, gain: 0.35, dest: d });
        for (let i = 0; i < 2; i++) ringTick(mx, d, t + 0.25 + i * 0.07, r(650, 800), 7, 0.25, 0.08); // hollow cases
        for (let i = 0; i < 2; i++) click(t + sec * (0.45 + 0.22 * i), r(1300, 1600), 0.35, 3);
        click(t + sec * 0.9, 900, 0.85, 3); // snap shut
        tone(mx, { type: 'sine', f0: 150, f1: 70, sweepTime: 0.05, t: t + sec * 0.9, dur: 0.09, gain: 0.45, dest: d });
      }
    });
  }

  /** Hammer falling on an empty chamber: a small, dry metal click. */
  dryFire(): void {
    const mx = this.mx;
    const p = jitterRatio(80);
    this.shot(null, mx.buses.weapons, 0.65, 0.25, (d, t) => {
      ringTick(mx, d, t, 3400 * p, 9, 0.55, 0.035);
      ringTick(mx, d, t + 0.004, 1700 * p, 5, 0.25, 0.03);
    });
  }

  /** A scream across the street: a wobbling, falling formant cry. */
  scream(pos: V2): void {
    const mx = this.mx;
    const f = r(620, 980);
    this.shot(pos, mx.buses.voice, 0.45, 1.2, (d, t) => {
      tone(mx, { type: 'sawtooth', f0: f, f1: f * 0.62, sweepTime: 0.8, t, dur: 0.85, gain: 0.3, attack: 0.04, release: 0.25, bp: f * 2.2, bpTo: f * 1.4, q: 3.5, detune: 12, dest: d });
      tone(mx, { type: 'triangle', f0: f * 1.5, f1: f * 0.9, sweepTime: 0.8, t, dur: 0.8, gain: 0.1, attack: 0.05, release: 0.25, dest: d });
      noiseBurst(mx, { kind: 'pink', t, dur: 0.7, gain: 0.07, attack: 0.05, filter: 'bandpass', f0: 2500, q: 1.5, dest: d });
    }, 30, 0.2);
  }

  /** Low grunt-ish blip (player hurt). */
  grunt(amount: number): void {
    const mx = this.mx;
    const a = clamp01(amount / 30);
    this.shot(null, mx.buses.voice, 0.4 + 0.4 * a, 0.35, (d, t) => {
      tone(mx, { type: 'sawtooth', f0: r(115, 135), f1: 80, sweepTime: 0.14, t, dur: 0.18, gain: 0.5, attack: 0.012, lp: 520, q: 1.5, dest: d });
      noiseBurst(mx, { kind: 'pink', t, dur: 0.12, gain: 0.12, attack: 0.02, filter: 'bandpass', f0: 600, q: 1.4, dest: d });
    });
  }

  /** Band-limited "electric horn" wave: odd-heavy partials, gently rolled off (built once). */
  private hornPeriodicWave(ctx: AudioContext): PeriodicWave {
    if (this.hornWave) return this.hornWave;
    const n = 16;
    const real = new Float32Array(n + 1);
    const imag = new Float32Array(n + 1);
    for (let k = 1; k <= n; k++) imag[k] = (k % 2 ? 1 : 0.35) / Math.pow(k, 1.15);
    this.hornWave = ctx.createPeriodicWave(real, imag);
    return this.hornWave;
  }

  /**
   * Hold-to-sound horn: a period electric horn — two notes a third apart, band-limited, through a
   * trumpet-ish resonance, with the diaphragm's buzz, a tiny pitch sag on the attack and a little
   * slap-back off the street.
   */
  setHorn(on: boolean, pitch = 1): void {
    const ctx = this.mx.ctx;
    if (!ctx || !this.mx.running) {
      if (!on) this.horn = null;
      return;
    }
    const now = ctx.currentTime;
    if (on && !this.horn) {
      const wave = this.hornPeriodicWave(ctx);
      const o1 = ctx.createOscillator();
      const o2 = ctx.createOscillator();
      o1.setPeriodicWave(wave);
      o2.setPeriodicWave(wave);
      const f1 = 392 * pitch;
      const f2 = 494 * pitch;
      o1.frequency.setValueAtTime(f1 * 1.03, now);
      o1.frequency.exponentialRampToValueAtTime(f1, now + 0.05);
      o2.frequency.setValueAtTime(f2 * 1.03, now);
      o2.frequency.exponentialRampToValueAtTime(f2, now + 0.05);
      o2.detune.value = 6;
      const res = ctx.createBiquadFilter();
      res.type = 'peaking';
      res.frequency.value = 1350;
      res.Q.value = 1.4;
      res.gain.value = 5;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 3200;
      lp.Q.value = 0.6;
      const am = ctx.createGain();
      am.gain.value = 0.88;
      const buzz = ctx.createOscillator();
      buzz.frequency.value = 72;
      const buzzDepth = ctx.createGain();
      buzzDepth.gain.value = 0.12;
      buzz.connect(buzzDepth).connect(am.gain);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.15, now + 0.025);
      const send = ctx.createGain();
      send.gain.value = 0.35;
      o1.connect(res);
      o2.connect(res);
      res.connect(lp).connect(am).connect(g).connect(this.mx.buses.sfx);
      g.connect(send).connect(this.mx.buses.echo);
      o1.start(now);
      o2.start(now);
      buzz.start(now);
      this.horn = { o1, o2, buzz, g, nodes: [o1, o2, buzz, buzzDepth, res, lp, am, g, send] };
    } else if (!on && this.horn) {
      const h = this.horn;
      this.horn = null;
      h.g.gain.cancelScheduledValues(now);
      h.g.gain.setTargetAtTime(0, now, 0.03);
      h.o1.stop(now + 0.2);
      h.o2.stop(now + 0.2);
      h.buzz.stop(now + 0.2);
      h.o1.onended = () => {
        for (const n of h.nodes) n.disconnect();
      };
    }
  }

  get hornOn(): boolean {
    return this.horn !== null;
  }

  /**
   * Somebody else's car horn: a short period toot (or two) at the car, for traffic swerving round you or
   * stuck behind. Same band-limited horn wave as ours, pitched per car.
   */
  toot(pos: V2, pitch = 1, double = false): void {
    const mx = this.mx;
    const ctx = mx.ctx;
    if (!ctx) return;
    const wave = this.hornPeriodicWave(ctx);
    const p = pitch * jitterRatio(60);
    this.shot(
      pos,
      mx.buses.sfx,
      0.32,
      0.9,
      (d, t) => {
        const blast = (at: number, dur: number): void => {
          tone(mx, { wave, f0: 392 * p * 1.03, f1: 392 * p, sweepTime: 0.05, t: at, dur, gain: 0.5, attack: 0.02, release: 0.05, bp: 1350, q: 0.9, dest: d });
          tone(mx, { wave, f0: 494 * p * 1.03, f1: 494 * p, sweepTime: 0.05, t: at, dur, gain: 0.4, attack: 0.02, release: 0.05, bp: 1350, q: 0.9, detune: 6, dest: d });
        };
        blast(t, r(0.16, 0.3));
        if (double) blast(t + r(0.3, 0.38), r(0.14, 0.22));
      },
      26,
      0.2,
    );
  }

  /**
   * A copper's pea whistle: a ~3 kHz tone trilled by the pea (fast detune wobble), breath under it.
   * `blasts` are the lengths of each blast (a warning is one short one, a chase a short-long).
   */
  whistle(pos: V2, blasts: readonly number[]): void {
    const mx = this.mx;
    const f = r(2750, 3150);
    const total = blasts.reduce((a, b) => a + b + 0.09, 0);
    this.shot(
      pos,
      mx.buses.voice,
      0.42,
      total + 0.3,
      (d, t) => {
        let at = t;
        for (const len of blasts) {
          tone(mx, { type: 'sine', f0: f * 0.97, f1: f, sweepTime: 0.04, t: at, dur: len, gain: 0.5, attack: 0.012, release: 0.04, vib: { hz: r(26, 34), cents: 70 }, dest: d });
          tone(mx, { type: 'sine', f0: f * 2.01, t: at, dur: len, gain: 0.06, attack: 0.02, release: 0.04, vib: { hz: 30, cents: 70 }, dest: d });
          noiseBurst(mx, { kind: 'pink', t: at, dur: len, gain: 0.12, attack: 0.01, release: 0.04, filter: 'bandpass', f0: f, q: 2.2, dest: d });
          at += len + 0.09;
        }
      },
      30,
      0.3,
    );
  }

  /** A body hitting the cobbles (a driver dragged out, a carjack scuffle). */
  bodyFall(pos: V2): void {
    const mx = this.mx;
    const b = jitterRatio(120);
    this.shot(pos, mx.buses.sfx, 0.55, 0.5, (d, t) => {
      tone(mx, { type: 'sine', f0: 95 * b, f1: 45 * b, sweepTime: 0.1, t, dur: 0.2, gain: 0.8, attack: 0.002, dest: d });
      noiseBurst(mx, { kind: 'brown', t, dur: 0.14, gain: 0.45, filter: 'lowpass', f0: 700, dest: d });
      noiseBurst(mx, { kind: 'pink', t: t + r(0.05, 0.09), dur: 0.06, gain: 0.15, filter: 'bandpass', f0: 1200, q: 1, dest: d });
    });
  }

  /**
   * One footstep: a heel thud plus the surface (pavement scuff, a clack on the setts, a crunch of
   * cinders, a slap on quay stone) and, when wet, a splash. `pos` null = the player.
   */
  footstep(pos: V2 | null, intensity: number, surface: Surface, wet: number, level = 1): void {
    const mx = this.mx;
    const i = clamp01(intensity);
    const w = clamp01(wet);
    this.stepFlip = !this.stepFlip;
    const alt = (this.stepFlip ? 1 : 0.92) * jitterRatio(60);
    this.shot(pos, mx.buses.sfx, (0.3 + 0.3 * i) * level, 0.25, (d, t) => {
      tone(mx, { type: 'sine', f0: 105 * alt, f1: 62, sweepTime: 0.04, t, dur: 0.06, gain: 0.32, attack: 0.001, dest: d });
      switch (surface) {
        case 'setts':
          ringTick(mx, d, t, r(1900, 2400) * alt, 4, 0.35, 0.03);
          ringTick(mx, d, t + r(0.015, 0.03), r(2600, 3100), 5, 0.12, 0.02);
          break;
        case 'grit':
          for (let k = 0; k < 4; k++) noiseBurst(mx, { kind: 'white', t: t + k * r(0.008, 0.02), dur: 0.018, gain: 0.22 * r(0.6, 1), filter: 'bandpass', f0: r(2500, 4200), q: 1, dest: d });
          noiseBurst(mx, { kind: 'brown', t, dur: 0.05, gain: 0.18, filter: 'lowpass', f0: 900, dest: d });
          break;
        case 'stone':
          ringTick(mx, d, t, r(1300, 1600) * alt, 3, 0.3, 0.035);
          break;
        default:
          noiseBurst(mx, { kind: 'pink', t, dur: 0.045, gain: 0.42, filter: 'bandpass', f0: r(900, 1150) * alt, q: 0.9, dest: d });
      }
      if (w > 0.05) {
        noiseBurst(mx, { kind: 'pink', t: t + 0.008, dur: 0.07 + 0.04 * i, gain: 0.2 * w * (0.6 + 0.6 * i), attack: 0.006, filter: 'bandpass', f0: r(1900, 3000), q: 1.1, dest: d });
        tone(mx, { type: 'sine', f0: r(500, 700), f1: 300, sweepTime: 0.03, t: t + 0.01, dur: 0.04, gain: 0.06 * w, dest: d });
      }
    });
  }

  /**
   * Fallback cadence footsteps for the player when no animation footstep bus is available
   * (headless, or a view without one).
   */
  footsteps(dt: number, moving: boolean, running: boolean, surface: Surface = 'pavement', wet = 0): void {
    if (!moving) {
      this.stepClock = 0;
      return;
    }
    this.stepClock -= dt;
    if (this.stepClock > 0) return;
    this.stepClock = running ? 0.31 : 0.5;
    this.footstep(null, running ? 0.8 : 0.3, surface, wet);
  }

  dispose(): void {
    this.setHorn(false);
    this.horn = null;
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
  }
}
